/**
 * Owl tattoos — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tattoo_subjects`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const owlTattoosPack: ToilePack = {
    slug: 'owl-tattoos',
    category: 'tattoo',
    name: 'Owl tattoos',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'perched', label: 'Perched owl', subject: 'an owl perched on a branch with a few leaves, wings folded, staring straight ahead with large round eyes'},
        {key: 'flight', label: 'Owl in flight', subject: 'an owl in flight seen from the front, wings spread wide, legs tucked, the feathers drawn row by row'},
        {key: 'mandala', label: 'Mandala owl', subject: "an owl's face whose eyes are two round mandalas and whose chest feathers are rows of petals and dots"},
    ],
    styles: [
        {key: 'traditional', label: 'Traditional', style: 'American traditional tattoo flash, bold black outlines, heavy black shading and a limited flat palette', palette: 'red, yellow and green accents with black', framing: 'centered with a wide white margin'},
        {key: 'fineline', label: 'Fine line', style: 'a single-needle fine line tattoo drawing, one uniform hairline stroke, delicate detail and almost no shading', palette: 'black ink only', framing: 'centered with a wide white margin'},
        {key: 'dotwork', label: 'Dotwork', style: 'a dotwork tattoo drawing, a thin outline with all shading built from stippled dots and dot gradients', palette: 'black ink only', framing: 'centered with a wide white margin, the dots reading cleanly'},
    ],
    directions: ['a barn owl', 'red ink', 'with a moon'],
    directionHint: 'try: a barn owl',
    seo: {
        title: 'Owl tattoo designs: perched owl, owl in flight, mandala owl',
        metaDescription: 'Nine owl tattoo designs: a perched owl, an owl in flight and an owl with mandala eyes, in three styles.',
        h1: 'Owl tattoo designs: perched owl, owl in flight, mandala owl',
        tagline: 'Three owls, each drawn in traditional flash, fine line and dotwork on white paper.',
        intro: 'Owl tattoo designs, worn for wisdom and for the night, on the forearm, the thigh or the back of the arm. The nine sheets show an owl perched on a branch, an owl in flight seen from the front, and an owl whose eyes are two mandalas. Each is drawn as traditional flash, fine line and dotwork. Type a different owl or twist, such as a barn owl, and the same styles can be asked for.',
        useCases: [
            'a forearm piece with a perched owl',
            'a thigh or back-of-arm owl in flight',
            'a mandala-eyed owl for a larger piece',
            'a printed sheet to compare styles',
        ],
        faq: [
            {q: 'What is on the page?', a: 'Nine owl designs: a perched owl, an owl in flight and a mandala owl, each in three styles.'},
            {q: 'Is this a design on skin?', a: 'No. Each design is a drawing on white paper, for taking to a studio or printing.'},
            {q: 'Can I ask for a barn owl or a moon?', a: 'Yes. Type a different owl or twist, such as a barn owl; the result is a new drawing, not a guaranteed match.'},
            {q: 'What is the difference between the styles?', a: 'Traditional flash has bold outlines and a few accent colours. Fine line is one uniform hairline with little shading. Dotwork builds the shadows from dots.'},
        ],
    },
}
